import BigNumber from 'bignumber.js';

// This is a constant string for marking the start and end of a subscript.
const SUBSCRIPT = '<subscript>';

// This is a constant string for marking the start and end of a bold symbol.
const BOLD = '<bold>';

// This object maps numbers to their corresponding subscript representations.
const subscriptMap: { [key: string]: string } = {
  '0': '₀',
  '1': '₁',
  '2': '₂',
  '3': '₃',
  '4': '₄',
  '5': '₅',
  '6': '₆',
  '7': '₇',
  '8': '₈',
  '9': '₉',
};

// Convert a number to its subscript representation.
const numberToSubscriptSymbol = (value: number) =>
  value
    .toString() // Convert the number to a string
    .split('') // Split the string into an array of characters
    .map((char) => subscriptMap[char]) // Map each character to its subscript representation
    .join(''); // Join the characters back into a string

// Encloses a number between SUBSCRIPT constants.
const numberToSubscript = (value: number) => `${SUBSCRIPT}${value}${SUBSCRIPT}`;

// Adds BOLD constant to the start of the string.
const symbolToBold = (value: string) => `${BOLD}${value}`;

// Constants representing large numerical values for easy reference.
const TRILLION = 1_000_000_000_000;
const BILLION = 1_000_000_000;
const MILLION = 1_000_000;
const THOUSAND = 1000;

// Formats positive numbers into a more readable format. For example: 1,200,000 becomes 1.2M.
const formatPositive = (value: number, precision: number, symbol: boolean) => {
  const bigNum = BigNumber(value); // Convert the number to a BigNumber for precise operations

  // Check the range of the number and convert it into T, B, M, or K format.
  // If the number is in trillions or more, return its value in trillions.
  if (bigNum.gte(TRILLION) || bigNum.lte(-TRILLION)) {
    const convertedSymbol = symbol ? 'T' : symbolToBold('T');
    return `${bigNum
      .div(TRILLION)
      .toNumber()
      .toFixed(precision)}${convertedSymbol}`;
  }
  // If the number is in billions or more, return its value in billions.
  if (bigNum.gte(BILLION) || bigNum.lte(-BILLION)) {
    const convertedSymbol = symbol ? 'B' : symbolToBold('B');
    return `${bigNum
      .div(BILLION)
      .toNumber()
      .toFixed(precision)}${convertedSymbol}`;
  }
  // If the number is in millions or more, return its value in millions.
  if (bigNum.gte(MILLION) || bigNum.lte(-MILLION)) {
    const convertedSymbol = symbol ? 'M' : symbolToBold('M');
    return `${bigNum
      .div(MILLION)
      .toNumber()
      .toFixed(precision)}${convertedSymbol}`;
  }
  // If the number is in the hundreds of thousands, return its value in thousands.
  if (bigNum.gte(THOUSAND * 100) || bigNum.lte(-THOUSAND * 100)) {
    const convertedSymbol = symbol ? 'K' : symbolToBold('K');
    return `${bigNum
      .div(THOUSAND)
      .toNumber()
      .toFixed(precision)}${convertedSymbol}`;
  }
  // Otherwise, return the number as is.
  return `${bigNum.toNumber().toFixed(precision)}`;
};

// Formats negative numbers, especially very small ones, to improve readability.
const formatNegative = (
  value: number,
  precision: number,
  symbol: boolean
): string => {
  // For future operations make the value absolute and convert it to a string.
  const absStringValue = Math.abs(value).toString();
  const sign = value > 0 ? '' : '-';

  // If the number is in scientific notation (contains 'e'), process it.
  const hasExp = absStringValue.includes('e');

  if (hasExp) {
    // Split the number into its mantissa and exponent parts.
    const [left, right] = absStringValue.replace('.', '').split('e-');
    let numbers = Number(`0.${left}`).toFixed(precision);
    if (Number(numbers) === 1) {
      return formatNegative(
        Number(`${sign}1e-${Number(right) - 1}`),
        precision,
        symbol
      );
    }
    numbers = numbers.slice(2);
    const convertedZeroPlaces = symbol
      ? numberToSubscriptSymbol(Number(right) - 1)
      : numberToSubscript(Number(right) - 1);
    return `${sign}0.0${convertedZeroPlaces}${numbers}`;
  }

  // Calculate the number of leading zeros for very small numbers.
  const zeroPlaces = -Math.floor(Math.log(Math.abs(value)) / Math.log(10) + 1);
  const firstIntIndex = zeroPlaces + 1;

  if (firstIntIndex > precision) {
    // Get decimals from splitting the value into its integer and decimal parts.
    const decimals = absStringValue.split('.')[1] || '';
    let numbers = Number(`0.${decimals.slice(zeroPlaces)}`).toFixed(precision);
    if (Number(numbers) === 1) {
      return formatNegative(
        Number(`${sign}1e-${zeroPlaces}`),
        precision,
        symbol
      );
    }
    numbers = numbers.slice(2);

    const convertedZeroPlaces = symbol
      ? numberToSubscriptSymbol(zeroPlaces)
      : numberToSubscript(zeroPlaces);
    return `${sign}0.0${convertedZeroPlaces}${numbers}`;
  }

  // If none of the above conditions are met, return the number as is.
  return `${value.toFixed(precision)}`;
};

// This is the main function that decides how to format a number based on its value.
const roundToPrecision = (value = 0, symbol = false, round = 4) => {
  // If the number is not valid or infinite, return 0 with the desired precision.
  if (!value || !isFinite(value)) {
    return Number(0).toFixed(round);
  }

  // If the number is positive or negative but greater than 1 in magnitude, format it using the formatPositive function.
  if (value >= 1 || value <= -1) {
    return formatPositive(value, round, symbol);
  }

  // Otherwise, format the number using the formatNegative function.
  return formatNegative(value, round, symbol);
};

// truncate decimals without side-effects of rouding
// truncateDecimals(1.267777331225157671, 4) will return 1.2677 etc
const truncateDecimals = (amount: number, decimals: number) =>
  Math.floor(amount * 10 ** decimals) / 10 ** decimals || 0.0;

const pricePrecision = (price: number) => {
  if (price >= 1 || price <= -1) {
    if (price >= 1000 || price <= -1000) {
      return 2;
    }
    return 4;
  }

  // Calculate the number of leading zeros for very small numbers.
  const zeroPlaces = -Math.floor(Math.log(Math.abs(price)) / Math.log(10) + 1);
  if (zeroPlaces < 4) {
    return zeroPlaces + 4;
  }
  return 4;
};

const chartPrecision = (price: number) => {
  if (price > 1000) {
    return 2;
  }
  if (price > 10) {
    return 4;
  }
  if (price < 1e-6) {
    return 14;
  }
  return 8;
};

const trimZeros = (value: string) => {
  const parsedValue = value.replace(/0+$/, '');
  if (parsedValue[parsedValue.length - 1] === '.') {
    return parsedValue.slice(0, parsedValue.length - 1);
  }
  return parsedValue;
};

// formats number to show without exponent and with specific number of significant (not zero) digits
const formatToRawNumber = (
  significantDigits: number,
  group: boolean,
  value?: number
) => {
  if (!value) {
    return '0';
  }
  return value.toLocaleString('fullwide', {
    useGrouping: group,
    maximumSignificantDigits: significantDigits > 21 ? 21 : significantDigits,
  });
};

export {
  SUBSCRIPT,
  BOLD,
  roundToPrecision,
  truncateDecimals,
  pricePrecision,
  chartPrecision,
  trimZeros,
  formatToRawNumber,
};
